import type {
  ModelRecordSubscription,
  ModelRecordSubscriptions,
} from '@app/api/Subscriptions';
import type { SubscriptionSaleDraftItem } from '@app/components/RecordModal/components/RecordDetails/types';
import { createId } from '@app/utils/createId';

// Проданный абонемент хранит Cost уже за вычетом скидки (beauty-crm
// LogicSubscriptions.SellSubscriptionToClient), поэтому цена строки без
// скидки — Cost + Discount, а «К оплате» снова равно Cost.
export const mapRecordSubscriptionsToDraftItems = (
  recordSubscriptions: ModelRecordSubscriptions | null | undefined
): SubscriptionSaleDraftItem[] =>
  (recordSubscriptions?.Subscriptions ?? []).map(
    ({
      Id,
      SubscriptionId,
      Name,
      SellerId,
      Cost,
      Discount,
      CanEditAndDelete,
    }) => {
      const price = Cost + Discount;

      return {
        id: createId(),
        saleId: Id,
        subscriptionId: SubscriptionId,
        name: Name,
        price,
        discountCurrency: Discount,
        discountPercent: price > 0 ? (Discount / price) * 100 : 0,
        sellerId: SellerId,
        canEditAndDelete: CanEditAndDelete,
      };
    }
  );

// SetRecordSubscriptions получает весь список абонементов записи: строки с
// Id = 0 продаются, проданные (Id ≠ 0) правятся, не попавшие в список
// удаляются. Оплаченные уходят без изменений, чтобы их не удалить.
export const buildRecordSubscriptionsPayload = (
  items: SubscriptionSaleDraftItem[] | undefined
): ModelRecordSubscription[] =>
  (items ?? [])
    .filter(
      (item): item is SubscriptionSaleDraftItem & { subscriptionId: number } =>
        Boolean(item.subscriptionId)
    )
    .map((item) => ({
      Id: item.saleId,
      SubscriptionId: item.subscriptionId,
      Name: item.name ?? null,
      SellerId: item.sellerId,
      Cost: item.price,
      Discount: item.discountCurrency,
      Count: 1,
      CanEditAndDelete: item.canEditAndDelete,
    }));

// Сохранять есть что: новые строки, удалённые или изменённые проданные.
export const hasRecordSubscriptionsChanges = (
  items: SubscriptionSaleDraftItem[] | undefined,
  savedItems: SubscriptionSaleDraftItem[]
): boolean => {
  const current = buildRecordSubscriptionsPayload(items);
  const saved = buildRecordSubscriptionsPayload(savedItems);

  if (current.some(({ Id }) => !Id) || current.length !== saved.length) {
    return true;
  }

  return saved.some((savedItem) => {
    const currentItem = current.find(({ Id }) => Id === savedItem.Id);

    return (
      !currentItem ||
      currentItem.Discount !== savedItem.Discount ||
      currentItem.SellerId !== savedItem.SellerId
    );
  });
};

export const validateRecordSubscriptions = (
  items: SubscriptionSaleDraftItem[] | undefined
): string | null => {
  const isDiscountInvalid = (items ?? []).some(
    ({ saleId, canEditAndDelete, subscriptionId, discountCurrency, price }) =>
      (!saleId || canEditAndDelete) &&
      Boolean(subscriptionId) &&
      (discountCurrency < 0 || discountCurrency > price)
  );

  return isDiscountInvalid ? 'Скидка абонемента указана некорректно' : null;
};

/** Абонементы записи после последнего успешного SetRecordSubscriptions */
export interface SavedRecordSubscriptions {
  recordId: number;
  items: SubscriptionSaleDraftItem[];
}

interface GetSavedRecordSubscriptionsParams {
  isEditMode: boolean;
  recordId: number | null | undefined;
  recordSubscriptions: ModelRecordSubscriptions | null | undefined;
  lastSaved: SavedRecordSubscriptions | null;
}

// Эталон абонементов для сравнения при «Сохранить». У новой записи абонементов
// нет. Ответ последнего успешного SetRecordSubscriptions по этой записи
// точнее record.Subscriptions: запись в слайсе не обновляется, если после
// абонементов упало сохранение записи доп. исполнителя.
export const getSavedRecordSubscriptions = ({
  isEditMode,
  recordId,
  recordSubscriptions,
  lastSaved,
}: GetSavedRecordSubscriptionsParams): SubscriptionSaleDraftItem[] => {
  if (!isEditMode) return [];

  if (lastSaved && recordId && lastSaved.recordId === recordId) {
    return lastSaved.items;
  }

  return mapRecordSubscriptionsToDraftItems(recordSubscriptions);
};

// Тексты ошибок сохранения абонементов: что случилось и что делать (UX-24).
// Без номера записи повторное «Сохранить» создаст запись заново, поэтому шаг —
// открыть запись из журнала.
export const RECORD_SUBSCRIPTIONS_NO_RECORD_ERROR =
  'Абонементы не сохранены. Откройте запись в журнале записей и добавьте абонементы ещё раз.';

export const getRecordSubscriptionsSaveErrorText = (reason: string): string => {
  const cleanReason = reason.trim().replace(/[.!\s]+$/, '');
  const head = cleanReason
    ? `Запись сохранена, но абонементы не сохранены: ${cleanReason}.`
    : 'Запись сохранена, но абонементы не сохранены.';

  return `${head} Нажмите «Сохранить» ещё раз.`;
};
